import { TreeViewSelectNode, TreeViewSelectOption } from './types';

function isSelectableNode(node: TreeViewSelectNode): boolean {
  return !node.children || node.children.length === 0;
}

function findNodeById(
  nodes: TreeViewSelectNode[],
  id: string,
): TreeViewSelectNode | undefined {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return undefined;
}

function collectSelectableNodes(
  nodes: TreeViewSelectNode[],
): TreeViewSelectNode[] {
  const selectableNodes: TreeViewSelectNode[] = [];

  function walk(items: TreeViewSelectNode[]) {
    for (const item of items) {
      if (isSelectableNode(item)) selectableNodes.push(item);
      else if (item.children) walk(item.children);
    }
  }

  walk(nodes);
  return selectableNodes;
}

export function getSelectableOptions(
  nodes: TreeViewSelectNode[],
): TreeViewSelectOption[] {
  return collectSelectableNodes(nodes).map(({ id, label }) => ({ id, label }));
}

export function filterToSelectableIds(
  selected: string[],
  nodes: TreeViewSelectNode[],
): string[] {
  const selectableIds = new Set(
    collectSelectableNodes(nodes).map(node => node.id),
  );
  return selected.filter(id => selectableIds.has(id));
}

export function getSelectableIdsUnder(
  itemId: string,
  nodes: TreeViewSelectNode[],
): string[] {
  const node = findNodeById(nodes, itemId);
  if (!node) return [];
  return collectSelectableNodes([node]).map(selectable => selectable.id);
}

export function toggleNodeSelection(
  itemId: string,
  current: string[],
  nodes: TreeViewSelectNode[],
): string[] {
  const selectableIds = getSelectableIdsUnder(itemId, nodes);
  const selected = new Set(current);
  const allSelected = selectableIds.every(id => selected.has(id));

  if (allSelected) selectableIds.forEach(id => selected.delete(id));
  else selectableIds.forEach(id => selected.add(id));

  return Array.from(selected);
}

export function expandSelectionWithParents(
  selected: string[],
  nodes: TreeViewSelectNode[],
): string[] {
  const selectedSet = new Set(selected);
  const result = new Set(selected);

  function walk(items: TreeViewSelectNode[]): boolean {
    let allSelected = true;

    for (const item of items) {
      if (isSelectableNode(item)) {
        if (!selectedSet.has(item.id)) allSelected = false;
        continue;
      }

      const childrenSelected = walk(item.children || []);
      if (childrenSelected) result.add(item.id);
      else allSelected = false;
    }

    return allSelected && items.length > 0;
  }

  walk(nodes);
  return Array.from(result);
}
